/**
 * At-rest encryption for the offline partition (OFFLINE_&_MOBILE_RESILIENCE
 * §9 device security). PIN-based, WebCrypto only — no dependencies:
 *
 * - A random 256-bit AES-GCM **data key** encrypts the local data. It is the
 *   only thing that ever touches plaintext records; it lives in memory after
 *   unlock and is never persisted in the clear.
 * - The user's **PIN** is stretched with PBKDF2-SHA-256 into a **wrapping
 *   key** that only ever wraps/unwraps the data key. Because the PIN wraps
 *   the data key rather than encrypting data directly, changing the PIN just
 *   re-wraps — no data is re-encrypted, and a forgotten PIN loses only the
 *   wrapping (the data key, hence the data, becomes unrecoverable by design).
 *
 * AES-GCM's authentication tag doubles as the wrong-PIN check: unwrap throws
 * on any wrong PIN or tampered ciphertext.
 */

const PBKDF2_ITERATIONS = 210_000
const IV_BYTES = 12
const SALT_BYTES = 16

const textEncoder = new TextEncoder()
const textDecoder = new TextDecoder()

function subtle(): SubtleCrypto {
  const c = globalThis.crypto
  if (!c?.subtle) throw new Error('WebCrypto is unavailable in this environment.')
  return c.subtle
}

function toBase64(bytes: ArrayBuffer | Uint8Array): string {
  const view = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes)
  let binary = ''
  for (const byte of view) binary += String.fromCharCode(byte)
  return btoa(binary)
}

function fromBase64(value: string): Uint8Array<ArrayBuffer> {
  const binary = atob(value)
  const out = new Uint8Array(binary.length)
  for (let i = 0; i < binary.length; i++) out[i] = binary.charCodeAt(i)
  return out
}

/** A fresh random salt (store it plaintext next to the wrapped key). */
export function randomSalt(): string {
  return toBase64(crypto.getRandomValues(new Uint8Array(SALT_BYTES)))
}

/** Stretch a PIN into the AES-GCM key-wrapping key (never encrypts data). */
export async function deriveWrapKey(
  pin: string,
  salt: string,
  iterations: number = PBKDF2_ITERATIONS,
): Promise<CryptoKey> {
  const base = await subtle().importKey('raw', textEncoder.encode(pin), 'PBKDF2', false, ['deriveKey'])
  return subtle().deriveKey(
    { name: 'PBKDF2', salt: fromBase64(salt), iterations, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  )
}

/** A fresh random data key — extractable so it can be wrapped. */
export function generateDataKey(): Promise<CryptoKey> {
  return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, ['encrypt', 'decrypt'])
}

/** Wrap the data key under a PIN-derived wrapping key → `iv.ciphertext` b64. */
export async function wrapDataKey(dataKey: CryptoKey, wrapKey: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const raw = await subtle().exportKey('raw', dataKey)
  const wrapped = await subtle().encrypt({ name: 'AES-GCM', iv }, wrapKey, raw)
  return `${toBase64(iv)}.${toBase64(wrapped)}`
}

/** Unwrap the data key; throws on a wrong PIN or tampered blob (GCM tag). The
 * unwrapped key is non-extractable — it can decrypt data but never be
 * re-exported in the clear. */
export async function unwrapDataKey(wrapped: string, wrapKey: CryptoKey): Promise<CryptoKey> {
  const [ivB64, ctB64] = wrapped.split('.')
  if (!ivB64 || !ctB64) throw new Error('Malformed wrapped key.')
  const raw = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(ivB64) }, wrapKey, fromBase64(ctB64))
  return subtle().importKey('raw', raw, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt'])
}

/** Re-wrap the data key under a new wrapping key WITHOUT importing it as a
 * (non-extractable) CryptoKey — decrypt the old wrap to raw bytes, encrypt
 * those under the new PIN's wrapping key. Used by the change-PIN path so the
 * unlocked in-memory data key can stay non-extractable. Throws on wrong old
 * PIN. */
export async function rewrapDataKey(
  wrapped: string,
  oldWrapKey: CryptoKey,
  newWrapKey: CryptoKey,
): Promise<string> {
  const [ivB64, ctB64] = wrapped.split('.')
  if (!ivB64 || !ctB64) throw new Error('Malformed wrapped key.')
  const raw = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(ivB64) }, oldWrapKey, fromBase64(ctB64))
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const rewrapped = await subtle().encrypt({ name: 'AES-GCM', iv }, newWrapKey, raw)
  return `${toBase64(iv)}.${toBase64(rewrapped)}`
}

/** AES-GCM encrypt a UTF-8 string → `iv.ciphertext` base64. */
export async function encryptString(plaintext: string, dataKey: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, dataKey, textEncoder.encode(plaintext))
  return `${toBase64(iv)}.${toBase64(ct)}`
}

/** Inverse of encryptString; throws on a wrong key or tampered blob. */
export async function decryptString(ciphertext: string, dataKey: CryptoKey): Promise<string> {
  const [ivB64, ctB64] = ciphertext.split('.')
  if (!ivB64 || !ctB64) throw new Error('Malformed ciphertext.')
  const pt = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(ivB64) }, dataKey, fromBase64(ctB64))
  return textDecoder.decode(pt)
}

export async function encryptJson(value: unknown, dataKey: CryptoKey): Promise<string> {
  return encryptString(JSON.stringify(value ?? null), dataKey)
}

export async function decryptJson<T>(ciphertext: string, dataKey: CryptoKey): Promise<T> {
  return JSON.parse(await decryptString(ciphertext, dataKey)) as T
}

/** Encrypt raw bytes (e.g. a captured photo) → `iv.ciphertext` base64. */
export async function encryptBytes(bytes: ArrayBuffer, dataKey: CryptoKey): Promise<string> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES))
  const ct = await subtle().encrypt({ name: 'AES-GCM', iv }, dataKey, bytes)
  return `${toBase64(iv)}.${toBase64(ct)}`
}

/** Inverse of encryptBytes → the original bytes. */
export async function decryptBytes(ciphertext: string, dataKey: CryptoKey): Promise<ArrayBuffer> {
  const [ivB64, ctB64] = ciphertext.split('.')
  if (!ivB64 || !ctB64) throw new Error('Malformed ciphertext.')
  const buf = await subtle().decrypt({ name: 'AES-GCM', iv: fromBase64(ivB64) }, dataKey, fromBase64(ctB64))
  return buf
}

/** The in-memory codec the runtime hands to its stores once unlocked. */
export interface Codec {
  encrypt(plaintext: string): Promise<string>
  decrypt(ciphertext: string): Promise<string>
  encryptBytes(bytes: ArrayBuffer): Promise<string>
  decryptBytes(ciphertext: string): Promise<ArrayBuffer>
}

export function codecFor(dataKey: CryptoKey): Codec {
  return {
    encrypt: (plaintext) => encryptString(plaintext, dataKey),
    decrypt: (ciphertext) => decryptString(ciphertext, dataKey),
    encryptBytes: (bytes) => encryptBytes(bytes, dataKey),
    decryptBytes: (ciphertext) => decryptBytes(ciphertext, dataKey),
  }
}

/**
 * Base64 SHA-256 of bytes about to be uploaded (audit FF-08).
 *
 * The server signs this into the presigned PUT and the sync engine echoes it as
 * `x-amz-checksum-sha256`, so R2 verifies the body and reports the digest back
 * — which is what populates `attachments.checksum_sha256` on the direct-to-R2
 * path. Without it that column is NULL for every file uploaded in staging and
 * production, because those bytes never pass through the Worker.
 *
 * Returns undefined rather than throwing when WebCrypto is unavailable. The
 * feature is optional end to end: no digest declared ⇒ the server signs as it
 * did before ⇒ the upload is unchanged. Captured work must never be lost to a
 * checksum.
 */
export async function sha256Base64(bytes: ArrayBuffer): Promise<string | undefined> {
  try {
    const digest = await crypto.subtle.digest('SHA-256', bytes)
    let binary = ''
    for (const byte of new Uint8Array(digest)) binary += String.fromCharCode(byte)
    return btoa(binary)
  } catch {
    return undefined
  }
}
